/**
 * Janela de apuração de uma competência a partir do dia de fechamento.
 *
 * Nasceu no módulo de Ponto CLT (onde o dia de fechamento é da empresa) e passou a
 * ser compartilhado com os cronogramas de pagamento por hora, onde o dia vem do
 * padrão de contrato. `helpers/timeClock.js` reexporta tudo daqui.
 *
 * A competência continua sendo o mês calendário; o dia de fechamento só decide até
 * quando aquela competência é medida.
 */

/**
 * Competência do mês corrente, como 'YYYY-MM'.
 */
export function currentReference() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Soma dias a uma data de calendário. Feito ao meio-dia UTC para que o
 * resultado não escorregue de dia por causa do fuso.
 */
export function addDays(dateStr, days) {
  const date = new Date(`${dateStr}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Número de dias do mês (month é 1-12).
 */
export function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Data em que um mês fecha: o dia configurado, limitado ao último dia do mês.
 * Um fechamento dia 30 encerra em 28/02 num ano não bissexto.
 */
export function closingDateOf(year, month, closingDay) {
  const day = Math.min(closingDay, lastDayOfMonth(year, month));
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Período de uma competência 'YYYY-MM' segundo o dia de fechamento.
 *
 * Sem dia de fechamento (null) o período é o mês calendário — o comportamento
 * de sempre. Com fechamento 14, a competência 2026-08 vai de 15/07 a 14/08: o
 * período começa no dia seguinte ao fechamento do mês anterior, de forma que os
 * períodos são contíguos e nenhum dia fica de fora nem entra duas vezes.
 *
 * @returns {{ start: string, end: string }} 'YYYY-MM-DD', ambas inclusive
 */
export function periodFromReference(reference, closingDay) {
  const [year, month] = reference.split("-").map(Number);

  if (!closingDay) {
    const last = lastDayOfMonth(year, month);
    return {
      start: `${reference}-01`,
      end: `${reference}-${String(last).padStart(2, "0")}`,
    };
  }

  const previousMonth =
    month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };

  return {
    start: addDays(
      closingDateOf(previousMonth.year, previousMonth.month, closingDay),
      1
    ),
    end: closingDateOf(year, month, closingDay),
  };
}

/**
 * Período da competência de um cronograma de pagamento.
 * Atalho para quem tem reference_month/reference_year separados.
 */
export function periodFromCompetence(month, year, closingDay) {
  return periodFromReference(
    `${year}-${String(month).padStart(2, "0")}`,
    closingDay
  );
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' */
function fullDate(dateStr) {
  if (!dateStr) return "-";
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
}

/**
 * '2026-07-15', '2026-08-14' → '15/07/2026 a 14/08/2026'
 */
export function formatPeriodLabel(start, end) {
  if (!start || !end) return "-";
  return `${fullDate(start)} a ${fullDate(end)}`;
}

/**
 * Sufixo de nome de arquivo: '2026-07-15_a_2026-08-14'
 */
export function periodFileSuffix(start, end) {
  return `${start}_a_${end}`;
}

/**
 * Janela de apuração de um cronograma de pagamento — só existe para contratos por
 * hora cujo padrão tem dia de fechamento.
 *
 * O back manda period_start/period_end já prontos na listagem; o fallback calcula
 * a partir do padrão incluído na resposta, para as telas que carregam o cronograma
 * por outro endpoint.
 *
 * @returns {{ start: string, end: string }|null}
 */
export function schedulePeriod(schedule, contract) {
  const remuneration =
    schedule?.contract?.remuneration_type || contract?.remuneration_type;
  if (remuneration !== "HOURLY") return null;

  const closingDay =
    schedule?.contract?.contract_pattern?.closing_day ??
    contract?.contract_pattern?.closing_day ??
    null;
  if (!closingDay) return null;

  if (schedule.period_start && schedule.period_end) {
    return { start: schedule.period_start, end: schedule.period_end };
  }
  return periodFromCompetence(
    schedule.reference_month,
    schedule.reference_year,
    closingDay
  );
}

/**
 * Competência a que uma data pertence, dado o dia de fechamento: 'MM/AAAA'.
 *
 * Sem fechamento é o próprio mês da data. Com fechamento 20, um apontamento de
 * 25/08 já está fora da janela de agosto e conta para setembro.
 */
export function competenceOfDate(dateStr, closingDay) {
  if (!dateStr) return "-";
  let [year, month, day] = dateStr.split("-").map(Number);

  if (closingDay && day > Math.min(closingDay, lastDayOfMonth(year, month))) {
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return `${String(month).padStart(2, "0")}/${year}`;
}

/**
 * Texto do dia de fechamento configurado.
 */
export function closingDayLabel(closingDay) {
  return closingDay
    ? `fechamento dia ${closingDay}`
    : "fecha no último dia do mês";
}
